import { Check, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";
import {
  changeLanguage,
  currentLanguage,
  SUPPORTED_LANGUAGES,
  type Language,
} from "@/shared/i18n";
import { LANG_LABEL } from "@/shared/i18n/alertPhrases";
import { Button } from "@/shared/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/shared/ui/DropdownMenu";

/**
 * LanguageMenu — display-language switcher (8 languages, 2026-09-01~).
 *
 * ko · en · zh · vi · th · id · mn · ja. Selecting a language switches the app
 * UI language *and* clears any voice-language override, so the spoken alarm
 * follows the UI language (the alarm overlay still offers a voice-only override
 * across the broadcast set for the rare case an operator wants a different
 * spoken language — 표시 언어와 방송 언어는 별개의 축이다).
 *
 * 메뉴에 뜨는 네이티브 표기(`LANG_LABEL`)는 alertPhrases 의 것을 그대로 재사용한다.
 * 같은 언어를 두 곳에서 다르게 적는 사고를 막기 위해 사본을 만들지 않는다.
 * 여기서 별도로 두는 것은 트리거 버튼에 들어갈 2글자 약어(`SHORT`)뿐이다.
 */

/** 2-letter badge shown on the trigger + row tail. ISO 639-1, uppercased. */
const SHORT: Record<Language, string> = {
  ko: "KO",
  en: "EN",
  zh: "ZH",
  vi: "VI",
  th: "TH",
  id: "ID",
  mn: "MN",
  ja: "JA",
};

export function LanguageMenu() {
  // Subscribing via useTranslation forces a re-render on language change so the
  // trigger label + active check stay in sync.
  const { t, i18n } = useTranslation();
  void i18n.resolvedLanguage;
  const lang = currentLanguage();
  const { setLangOverride } = useAlertTTS();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          intent="ghost"
          size="sm"
          aria-label={t("widgets.languageMenu.label")}
          title={t("widgets.languageMenu.label")}
          className="gap-1.5 px-2 text-[12px] font-semibold tracking-tight"
        >
          <Languages size={15} strokeWidth={1.75} />
          {SHORT[lang]}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        <DropdownMenuLabel>{t("widgets.languageMenu.label")}</DropdownMenuLabel>
        {SUPPORTED_LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onSelect={() => {
              changeLanguage(lng);
              setLangOverride(null); // voice follows the UI language
            }}
          >
            <span className="inline-flex w-4 justify-center">
              {lang === lng && <Check size={13} strokeWidth={2.4} />}
            </span>
            {LANG_LABEL[lng]}
            <span className="ml-auto text-[11px] tabular-nums text-[var(--fg-subtle)]">
              {SHORT[lng]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
